import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FileText, BookOpen, Video, Download, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const Resources = () => {
  const resources = [
    {
      icon: <FileText size={32} className="text-[#0E7490]" />,
      color: 'teal',
      type: 'Whitepaper',
      title: 'The Future of Credit Underwriting: From Static Scores to Dynamic Intelligence',
      description: 'How AI-native platforms are transforming lending decisions in BFSI',
      date: 'Dec 2024',
      action: 'Download'
    },
    {
      icon: <BookOpen size={32} className="text-[#4F46E5]" />,
      color: 'purple',
      type: 'Blog',
      title: 'Understanding Agentic AI in Financial Services',
      description: 'A deep dive into multi-agent systems for high-stakes lending',
      date: 'Nov 2024',
      action: 'Read More'
    },
    {
      icon: <FileText size={32} className="text-[#1A56DB]" />,
      color: 'blue',
      type: 'Case Study',
      title: 'How a Leading Bank Reduced Underwriting Time by 85%',
      description: 'Real-world implementation of Financial Intelligence Platform',
      date: 'Oct 2024',
      action: 'Download'
    },
    {
      icon: <Video size={32} className="text-[#B45309]" />,
      color: 'amber',
      type: 'Webinar',
      title: 'Navigating Regulatory Compliance in AI-Powered Lending',
      description: 'Best practices for explainable AI in regulated environments',
      date: 'Sep 2024',
      action: 'Watch'
    }
  ];

  const colorMap = {
    teal: { bg: 'bg-[#0E7490]/10', text: 'text-[#0E7490]', btn: 'border-[#0E7490] text-[#0E7490] hover:bg-[#0E7490]' },
    purple: { bg: 'bg-[#4F46E5]/10', text: 'text-[#4F46E5]', btn: 'border-[#4F46E5] text-[#4F46E5] hover:bg-[#4F46E5]' },
    blue: { bg: 'bg-[#1A56DB]/10', text: 'text-[#1A56DB]', btn: 'border-[#1A56DB] text-[#1A56DB] hover:bg-[#1A56DB]' },
    amber: { bg: 'bg-[#B45309]/10', text: 'text-[#B45309]', btn: 'border-[#B45309] text-[#B45309] hover:bg-[#B45309]' },
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Dark Navy */}
      <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-[#060D1B] via-[#0A1628] to-[#0d1f3c]">
        <div className="max-w-[1200px] mx-auto relative z-10">
          <div className="text-center" data-testid="resources-header">
            <h1 className="text-h1 text-white mb-6">Resources & Insights</h1>
            <p className="text-body-lg text-[#6B7280] max-w-3xl mx-auto">
              Thought Leadership: Deep dives into AI-powered financial intelligence and BFSI transformation
            </p>
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="section-padding px-6 bg-[#EFF6FF]">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid md:grid-cols-2 gap-8 mb-20">
            {resources.map((resource, index) => {
              const colors = colorMap[resource.color];
              return (
                <Card 
                  key={index} 
                  className="bg-white border-[#D1D5DB] rounded-xl hover:shadow-lg transition-all" 
                  data-testid={`resource-${index}`}
                >
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className={`p-3 ${colors.bg} rounded-lg`}>
                        {resource.icon}
                      </div>
                      <div className="flex-1">
                        <div className={`text-sm ${colors.text} font-semibold mb-1`}>{resource.type}</div>
                        <CardTitle className="text-[#0A1628] text-xl mb-2">{resource.title}</CardTitle>
                        <p className="text-[#6B7280] text-sm">{resource.description}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[#6B7280]">{resource.date}</span>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className={`border ${colors.btn} hover:text-white transition-colors`}
                        data-testid={`resource-action-${index}`}
                      >
                        {resource.action === 'Download' ? (
                          <>
                            <Download size={16} className="mr-2" />
                            Download
                          </>
                        ) : (
                          <>
                            {resource.action}
                            <ArrowRight size={16} className="ml-2" />
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Newsletter Section */}
          <div className="bg-[#0A1628] rounded-xl p-12 text-center" data-testid="newsletter-section">
            <h2 className="text-h3 text-white mb-4">
              Stay Ahead of the Curve
            </h2>
            <p className="text-[#6B7280] mb-8 max-w-2xl mx-auto">
              Get the latest insights on AI in BFSI, regulatory updates, and industry trends delivered to your inbox
            </p>
            
            <div className="max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <Input 
                  type="email" 
                  placeholder="Enter your email" 
                  className="bg-white/10 border-white/20 text-white placeholder:text-[#6B7280] focus:border-[#1A56DB] focus:ring-[#1A56DB]"
                  data-testid="newsletter-email-input"
                />
                <Button 
                  className="bg-[#1A56DB] hover:bg-[#1A56DB]/90 text-white font-semibold whitespace-nowrap btn-hover-lift w-full sm:w-auto"
                  data-testid="newsletter-subscribe-btn"
                >
                  Subscribe
                </Button>
              </div>
              <p className="text-sm text-[#6B7280] mt-3">
                We respect your privacy. Unsubscribe anytime.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding px-6 bg-gradient-to-r from-[#0A1628] to-[#1A56DB]">
        <div className="max-w-[1200px] mx-auto text-center">
          <h2 className="text-h2 text-white mb-6">
            Ready to Learn More?
          </h2>
          <p className="text-body-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Speak with our team to understand how Cognifin can transform your credit operations
          </p>
          <Button 
            asChild 
            size="lg" 
            className="bg-white hover:bg-white/90 text-[#0A1628] font-bold text-lg px-10 h-14 btn-hover-lift shadow-xl"
          >
            <Link to="/contact">Request a Demo</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Resources;
